import clsx from "clsx";
import type { FC } from "react";

import { InteractionMode } from "components/server-types";
import { ClassCollaborationIcon, PersonGroupIcon, PersonSingleIcon } from "mds/icons";

type InteractionModeIconVariant = "default" | "disabled" | "selected";

interface InteractionModeIconProps {
  interactionMode: InteractionMode;
  variant?: InteractionModeIconVariant;
}

const CIRCLE_CLASS = "flex h-6 w-6 flex-none items-center justify-center rounded-full";

const BG_CLASSES: Record<InteractionModeIconVariant, Record<string, string>> = {
  default: {
    individual: "bg-blue-tint-93",
    group: "bg-hibiscus-tint-95",
    shared: "bg-moss-tint-95",
  },
  disabled: {
    individual: "bg-black-tint-97",
    group: "bg-black-tint-97",
    shared: "bg-black-tint-97",
  },
  selected: {
    individual: "",
    group: "",
    shared: "",
  },
};

const ICON_CLASSES: Record<InteractionModeIconVariant, Record<string, string>> = {
  default: {
    individual: "text-blue",
    group: "text-hibiscus",
    shared: "text-moss",
  },
  disabled: {
    individual: "text-black-tint-55",
    group: "text-black-tint-55",
    shared: "text-black-tint-55",
  },
  selected: {
    individual: "text-white",
    group: "text-white",
    shared: "text-white",
  },
};

export const InteractionModeIcon: FC<InteractionModeIconProps> = ({
  interactionMode,
  variant = "default",
}) => {
  switch (interactionMode) {
    case InteractionMode.INDIVIDUAL:
      return (
        <div className={clsx(CIRCLE_CLASS, BG_CLASSES[variant].individual)}>
          <PersonSingleIcon className={ICON_CLASSES[variant].individual} size="xs" />
        </div>
      );
    case InteractionMode.ASSIGN_STUDENTS:
    case InteractionMode.COPY_GROUPS:
    case InteractionMode.SELF_ASSIGN:
      return (
        <div className={clsx(CIRCLE_CLASS, BG_CLASSES[variant].group)}>
          <PersonGroupIcon className={ICON_CLASSES[variant].group} size="xs" />
        </div>
      );
    case InteractionMode.SHARED_CLASS:
      return (
        <div className={clsx(CIRCLE_CLASS, BG_CLASSES[variant].shared)}>
          <ClassCollaborationIcon className={ICON_CLASSES[variant].shared} size="xs" />
        </div>
      );
    default:
      return null;
  }
};
